import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import styles from './OrderForward.module.scss';
import { iRootDispatch, iRootState, select } from '../../../../store/store';
import DataSource, { DataSourceOptions } from 'devextreme/data/data_source';
import ProgressBarBoxes, { RemainingTrailerSpace } from '../../progress-bar-boxes/ProgressBarBoxes';
import Button from 'fulogics-ui-web/dist/layout/dx/Button/Button';
import DataGrid from 'fulogics-ui-web/dist/layout/dx/DataGrid/DataGrid';
import withInspector from 'fulogics-ui-web/dist/layout/inspector/hoc/withInspector';
import { orderStepsForwardAddNewUnloadPlaceId, orderStepsForwardGridId } from './inspectorOptions';
import { OrderForwardStepData, OrderForwardStepDataKeys } from '../OrderForwardStepData';
import OrderForwardStepForm, {
    OrderForwardStepFormData,
    OrderForwardStepPayloadItemDataKeys,
} from '../order-forward-step-form/OrderForwardStepForm';
import {
    COSOrderForwardStepStore,
    insertOrderForwardStepToCollectionPayload,
} from '../../../../store/models/COSOrderForwardStepModel';
import { useMemoDataSource } from 'fulogics-ui-web/dist/layout/hooks/useMemoDataSource';
import enOrderStepType from '../../../../api/enums/enOrderStepType';
import { Template } from 'devextreme-react/core/template';
import ForwardStepTypeColumnTemplate from '../../../templates/dataGrid/column/forward-step-type/ForwardStepTypeColumnTemplate';
import { nameof } from 'ts-simple-nameof/src/nameof';
import { deleteItemPayload } from '../../../../store/models/COSOrderForwardStepModel';
import { COSOrderForwardStepPayloadStore } from '../../../../store/models/COSOrderForwardStepPayloadModel';
import enArrowDirection from '../../../../api/enums/enArrowDirection';
import { IdType } from 'fulogics-ui-core';
import ValidationInfoColumnTemplate from '../../../templates/dataGrid/column/validation-info-column-template/ValidationInfoColumnTemplate';
import OrderForwardPlannedStartDateColumnTemplate from '../../../templates/dataGrid/column/order-forward-planned-start-date-column-template/OrderForwardPlannedStartDateColumnTemplate';
import { OrderValidationResult } from '../../order-error-info/OrderErrorInfo';
import Validator, { AsyncRule, RangeRule, RequiredRule } from 'devextreme-react/validator';
import { orderEditValidationGroup } from '../inspectorOptions';
import { getRemainingForwardTrailerSpacePayload } from '../../../../store/models/COSOrdersModel';
import { getTrailerCapacity } from '../../progress-bar-boxes/getTrailerCapacity';

const selection = select((_models) => ({}));

const mapStateToProps = (state: iRootState) =>
    ({
        createForwardStepsCollectionDataSource: (forwardStepsCollectionId?: string) => {
            const opts = COSOrderForwardStepStore.createByCollectionId(forwardStepsCollectionId, {
                defaultOrderByPolicy: 'timetable',
            });
            if (opts) {
                opts.select = OrderForwardStepItemKeys;
            }

            return opts;
        },
        createPayloadDataSource: (payloadCollectionId?: string) => {
            let opts = COSOrderForwardStepPayloadStore.createByCollectionId(payloadCollectionId);
            //
            if (opts) opts.select = OrderForwardStepPayloadItemDataKeys;
            //
            return opts;
        },
        ...(selection(state) as any),
    } as OrderForwardDisplayOptions);

const mapDispatch = (_dispatch: iRootDispatch, _ownDispatch: OrderForwardDisplayOptions) =>
    ({
        // @ts-ignore
        insertOrderForwardStep: (payload?: insertOrderForwardStepToCollectionPayload) =>
            // @ts-ignore
            _dispatch.COSOrderForwardStep.insertToCollection<OrderForwardStepItem>({
                responseDataKeys: OrderForwardStepItemKeys,
                //
                ...payload,
            }),
        removeStep: _dispatch?.COSOrderForwardStep?.deleteItem,
        getRemainingTrailerSpace: _dispatch?.COSOrders?.getRemainingForwardTrailerSpace,
        ..._ownDispatch,
    } as OrderForwardDisplayOptions);

const OrderForward = connect(
    mapStateToProps,

    mapDispatch as any
)((props: OrderForwardProps) => {
    const { t } = useTranslation('OrderForward');
    const [remainingTrailerSpace, setRemainingTrailerSpace] =
        useState<RemainingTrailerSpace | undefined>();

    const orderStepsForwardDataSource = useMemoDataSource(
        () => props.createForwardStepsCollectionDataSource?.(props.data?.forwardSteps?.id),
        [props.data?.forwardSteps?.id]
    );

    const [focusedStep, setFocusedStep] = useState<OrderForwardStepData | undefined>();

    const PayloadDataSource = useMemoDataSource(
        () => props.createPayloadDataSource?.(focusedStep?.payload?.id),
        [focusedStep?.payload?.id]
    );

    const onUnloadPlaceAdd = useCallback(async () => {
        if (props.insertOrderForwardStep !== undefined) {
            let r = await props.insertOrderForwardStep({
                collectionId: props.data?.forwardSteps?.id,
            });
            //
            setFocusedStep(r);
        }
    }, [props.insertOrderForwardStep, props.data?.forwardSteps?.id]);

    const onOrderStepsForwardGridFocusedRowChanged = useCallback((e: any) => {
        const dataRow = e.row && e.row.data;

        if (!!dataRow) {
            setFocusedStep(dataRow);
        }
    }, []);

    const onForwardStepFormFieldDataChanged = useCallback(
        (dataField?: string, value?: any) => {
            if (dataField === nameof<OrderForwardStepFormData>((q) => q.payload)) {
                orderStepsForwardDataSource?.reload();
            }
        },
        [orderStepsForwardDataSource]
    );

    const reloadOrderStepsForwardDataSource = useCallback(() => {
        orderStepsForwardDataSource?.reload();
    }, [orderStepsForwardDataSource]);

    const removeStep = useCallback(
        async (args: deleteItemPayload) => {
            await PayloadDataSource?.load();
            await Promise.all(
                PayloadDataSource?.items().map(async (item) => {
                    return await PayloadDataSource?.store().remove(item.id);
                }) as Promise<any>[]
            );
            await props.removeStep?.(args);
            //
            setFocusedStep(undefined);
        },
        [props.removeStep, orderStepsForwardDataSource, focusedStep, PayloadDataSource]
    );

    const calculateRemainingSpace = useCallback(() => {
        if (props.getRemainingTrailerSpace && props.data?.id) {
            return props
                .getRemainingTrailerSpace({
                    id: props.data.id,
                })
                .then(setRemainingTrailerSpace);
        }

        return Promise.resolve();
    }, [props.getRemainingTrailerSpace]);

    useEffect(() => {
        const handleDataChange = () => {
            props?.validateOrder?.();
            calculateRemainingSpace();
        };

        orderStepsForwardDataSource?.on('changed', handleDataChange);
        return () => {
            orderStepsForwardDataSource?.off('changed', handleDataChange);
        };
    }, [orderStepsForwardDataSource, props?.validateOrder, calculateRemainingSpace]);

    useEffect(() => {
        calculateRemainingSpace();
    }, [calculateRemainingSpace]);

    const { filledTrailerCapacity, maxTrailerCapacity } = getTrailerCapacity(remainingTrailerSpace);

    const maxCapacityReached =
        filledTrailerCapacity > maxTrailerCapacity && !!props.data?.forwardSteps;

    const validateWithBackend = async (params: { value?: { id?: IdType }[] }) => {
        try {
            const validationResult = await props.validateOrder?.();

            const brokenRules = validationResult?.brokenRules?.filter(({ relatedEntityId }) =>
                params?.value?.some(({ id }) => id === relatedEntityId)
            );

            return !brokenRules?.length;
        } catch (e) {
            return false;
        }
    };

    const orderStepsForwardValidationAdapter = useMemo(
        () => ({
            getValue: () => orderStepsForwardDataSource?.items(),
        }),
        [orderStepsForwardDataSource]
    );

    const capacityValidationAdapter = useMemo(
        () => ({
            getValue: () => filledTrailerCapacity,
        }),
        [filledTrailerCapacity]
    );

    return (
        <div className={classNames(styles.container, props.className)}>
            <div className={classNames(styles.left)}>
                {focusedStep && (
                    <OrderForwardStepForm
                        data={focusedStep}
                        isReadOnly={focusedStep.type === enOrderStepType.Load}
                        onFieldDataChanged={onForwardStepFormFieldDataChanged}
                        removeStep={removeStep}
                        maxTrailerCapacity={maxTrailerCapacity}
                        onStepsForwardDataChanged={reloadOrderStepsForwardDataSource}
                        remainingTrailerSpace={remainingTrailerSpace}
                        calculateRemainingTrailerSpace={calculateRemainingSpace}
                        filledTrailerCapacity={filledTrailerCapacity}
                    />
                )}
            </div>
            <div className={classNames(styles.right)}>
                <div className={classNames(styles.rightContent)}>
                    <DataGrid
                        inspectorId={orderStepsForwardGridId}
                        dataSource={orderStepsForwardDataSource}
                        focusedRowEnabled
                        focusedRowKey={focusedStep?.id}
                        repaintChangesOnly
                        onFocusedRowChanged={onOrderStepsForwardGridFocusedRowChanged}
                        onRowUpdated={props?.validateOrder}
                    >
                        <Template name="ForwardStepType" render={ForwardStepTypeColumnTemplate} />
                        <Template
                            name="OrderForwardPlannedStartDateColumn"
                            render={OrderForwardPlannedStartDateColumnTemplate}
                        />
                        <Template
                            name="ValidationInfoColumn"
                            render={ValidationInfoColumnTemplate}
                        />
                    </DataGrid>
                    <Validator
                        validationGroup={orderEditValidationGroup}
                        adapter={orderStepsForwardValidationAdapter}
                    >
                        <AsyncRule validationCallback={validateWithBackend} />
                    </Validator>
                    <Validator
                        validationGroup={orderEditValidationGroup}
                        adapter={capacityValidationAdapter}
                    >
                        <RangeRule max={maxTrailerCapacity} />
                        <RequiredRule />
                    </Validator>
                    <Button
                        className={classNames(styles.addNavigationPlace)}
                        inspectorId={orderStepsForwardAddNewUnloadPlaceId}
                        onClick={onUnloadPlaceAdd}
                    />
                </div>
                <div className={classNames(styles.progressBarContainer)}>
                    <ProgressBarBoxes
                        arrowDirection={enArrowDirection.Right}
                        name={t('orderForwardProgressBarTitle')}
                        itemsCount={filledTrailerCapacity}
                        maxItemsCount={maxTrailerCapacity}
                        remainingTrailerSpace={remainingTrailerSpace}
                    />
                    {maxCapacityReached && (
                        <label className={styles.blockedButtonsLabel}>
                            {t('maxCapacityReached')}
                        </label>
                    )}
                </div>
            </div>
        </div>
    );
});

export default withInspector<OrderForwardProps>(OrderForward, {});

export type OrderForwardProps = {
    data?: OrderForwardData;
} & OrderForwardDisplayOptions;

export class OrderForwardDisplayOptions {
    className?: string;
    // Zasilanie dataGrid
    createForwardStepsCollectionDataSource?: (
        forwardStepsCollectionId?: string
    ) => DataSource | DataSourceOptions | undefined; // elementy typu OrderForwardStepData
    //
    insertOrderForwardStep?: (
        payload?: insertOrderForwardStepToCollectionPayload
    ) => Promise<OrderForwardStepItem>;
    removeStep?: (payload: deleteItemPayload) => Promise<void>;
    createPayloadDataSource?: (
        payloadCollectionId?: string
    ) => DataSource | DataSourceOptions | undefined;
    validateOrder?: () => Promise<OrderValidationResult | undefined>;
    getRemainingTrailerSpace?: (
        payload: getRemainingForwardTrailerSpacePayload
    ) => Promise<RemainingTrailerSpace | undefined>;
}

export type OrderForwardStepItem = OrderForwardStepData;
export const OrderForwardStepItemKeys = OrderForwardStepDataKeys;

export type OrderForwardData = {
    id?: string;
    forwardSteps?: {
        id?: string;
        filledTrailerCapacity?: number;
        maxTrailerCapacity?: number;
    };
};

export const OrderForwardDataKeys = [
    'id',
    'forwardSteps.id',
    'forwardSteps.filledTrailerCapacity',
    'forwardSteps.maxTrailerCapacity',
];
